// PRC-049 (boceto S4, diseño 33 apartado 4): «Actualizar» un seguimiento.
// Opciones: Compra recuperada — cerrar · Sin cambios — reprogramar revisión ·
// Causa identificada — archivar · Añadir nota. Todo queda en la cronología.
// El diálogo no escribe: devuelve la decisión a quien lo abre.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, InputBase } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ProaDatePicker from '../../../../components/Common/ProaDatePicker';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatInteger } from '../../../pricingJourney/formatNumber';
import type { MarginCenterFollowUpInventoryV2Item } from '../../types/followUpInventory';
import {
  CAUSE_REASONS,
  REVIEW_SHORTCUTS,
  formatDayDate,
  reviewDateFor,
  reviewDateToIso,
  toDateOnly,
  type ReviewShortcut,
} from './cadenceManagementTexts';

export type FollowUpUpdateChoice = 'recovered' | 'reschedule' | 'cause' | 'note';

export type FollowUpUpdateDecision =
  | { kind: 'recovered'; conclusion: string }
  | { kind: 'reschedule'; nextReviewIso: string }
  | { kind: 'cause'; reason: string; conclusion: string }
  | { kind: 'note'; content: string };

export const UPDATE_OPTIONS: Array<{ id: FollowUpUpdateChoice; label: string }> = [
  { id: 'recovered', label: 'Compra recuperada — cerrar' },
  { id: 'reschedule', label: 'Sin cambios — reprogramar revisión' },
  { id: 'cause', label: 'Causa identificada — archivar' },
  { id: 'note', label: 'Añadir nota' },
];

/** Conclusión al cerrar por compra recuperada. */
export const recoveredConclusion = (item: Pick<MarginCenterFollowUpInventoryV2Item, 'recovered_on' | 'recovered_units'>): string =>
  item.recovered_on
    ? `Compra recuperada el ${formatDayDate(item.recovered_on)}${item.recovered_units != null ? ` · ${formatInteger(item.recovered_units)} uds.` : ''}.`
    : 'Compra recuperada.';

const lblSx = { fontSize: 12, fontWeight: 700, color: J.body, m: '14px 0 6px', fontFamily: J.font } as const;
const inputSx = {
  width: '100%', border: '1px solid #334155', borderRadius: '10px', px: '12px', py: '7px',
  fontSize: 13.5, color: J.text, bgcolor: 'rgba(2,6,23,.5)', fontFamily: J.font,
} as const;

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={on}
    sx={{
      border: '1px solid', borderColor: on ? J.sky : '#334155', borderRadius: 999, px: '12px', py: '5px',
      fontSize: 12.5, fontWeight: 600, fontFamily: J.font, color: on ? J.skyText : J.body,
      bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
    }}
  >
    {children}
  </ButtonBase>
);

interface Props {
  item: MarginCenterFollowUpInventoryV2Item | null;
  initialChoice?: FollowUpUpdateChoice;
  onClose: () => void;
  onConfirm: (item: MarginCenterFollowUpInventoryV2Item, decision: FollowUpUpdateDecision) => void;
}

const FollowUpUpdateDialog: React.FC<Props> = ({ item, initialChoice = 'reschedule', onClose, onConfirm }) => {
  const [choice, setChoice] = useState<FollowUpUpdateChoice>(initialChoice);
  const [review, setReview] = useState<ReviewShortcut | null>(null);
  const [otherDate, setOtherDate] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [conclusion, setConclusion] = useState('');
  const [edited, setEdited] = useState(false);
  const [note, setNote] = useState('');
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!item) return;
    setChoice(initialChoice); setReview(null); setOtherDate(''); setReason(null);
    setConclusion(''); setEdited(false); setNote(''); setAttempted(false);
  }, [item, initialChoice]);

  useEffect(() => {
    if (!item || edited) return;
    if (choice === 'recovered') setConclusion(recoveredConclusion(item));
    else if (choice === 'cause' && reason) setConclusion(`${reason}.`);
  }, [choice, reason, item, edited]);

  const reviewDate = review === 'other' ? otherDate : review ? reviewDateFor(review) : null;
  const error = useMemo(() => {
    if (choice === 'reschedule' && !reviewDate) return 'Elige la nueva fecha de revisión.';
    if (choice === 'cause' && !reason) return 'Elige un motivo.';
    if ((choice === 'cause' || choice === 'recovered') && !conclusion.trim()) return 'Escribe la conclusión.';
    if (choice === 'note' && !note.trim()) return 'Escribe la nota.';
    return null;
  }, [choice, reviewDate, reason, conclusion, note]);

  if (!item) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (error) return;
    if (choice === 'recovered') onConfirm(item, { kind: 'recovered', conclusion: conclusion.trim() });
    else if (choice === 'reschedule') onConfirm(item, { kind: 'reschedule', nextReviewIso: reviewDateToIso(reviewDate!) });
    else if (choice === 'cause') onConfirm(item, { kind: 'cause', reason: reason!, conclusion: conclusion.trim() });
    else onConfirm(item, { kind: 'note', content: note.trim() });
  };

  const primary = choice === 'recovered' ? 'Cerrar seguimiento' : choice === 'reschedule' ? 'Reprogramar' : choice === 'cause' ? 'Archivar' : 'Añadir nota';
  const who = `${item.customer_label || item.customer_id || ''}${item.product_id ? ` · ${item.product_id}` : ''}`;

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: J.panel, backgroundImage: 'none', border: '1px solid rgba(148,163,184,.16)', borderRadius: '14px' } }}>
      <Box component="form" onSubmit={submit} sx={{ p: '20px 22px', fontFamily: J.font, color: J.text }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText }}>Actualizar seguimiento</Box>
            <Box component="h2" sx={{ fontSize: 17, fontWeight: 800, m: '4px 0 0' }}>{who}</Box>
            {item.next_action && <Box sx={{ fontSize: 12, color: J.muted, mt: '2px' }}>Próxima acción: {item.next_action}{item.next_review_at ? ` · revisión ${formatDayDate(item.next_review_at)}` : ''}</Box>}
          </Box>
          <IconButton aria-label="Cerrar" onClick={onClose} sx={{ width: 32, height: 32, border: '1px solid #334155', borderRadius: '10px', color: J.skyText }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '14px' }}>
          {UPDATE_OPTIONS.map((o) => (
            <Chip key={o.id} on={choice === o.id} onClick={() => { setChoice(o.id); setAttempted(false); setEdited(false); }}>{o.label}</Chip>
          ))}
        </Box>

        {choice === 'reschedule' && (
          <>
            <Box sx={lblSx}>Nueva fecha de revisión</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {REVIEW_SHORTCUTS.map((s) => {
                const on = review === s.id;
                const date = s.id !== 'other' ? reviewDateFor(s.id) : null;
                return <Chip key={s.id} on={on} onClick={() => setReview(s.id)}>{on && date ? `${s.label} · ${formatDayDate(date)}` : s.label}</Chip>;
              })}
            </Box>
            {review === 'other' && (
              <Box sx={{ mt: '10px', maxWidth: 260 }}>
                <ProaDatePicker label="Fecha de revisión" value={otherDate} onChange={setOtherDate} size="small" minDate={toDateOnly(new Date())} />
              </Box>
            )}
          </>
        )}

        {choice === 'cause' && (
          <>
            <Box sx={lblSx}>Motivo</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {CAUSE_REASONS.map((r) => <Chip key={r} on={reason === r} onClick={() => setReason(r)}>{r}</Chip>)}
            </Box>
          </>
        )}

        {(choice === 'cause' || choice === 'recovered') && (
          <>
            <Box sx={lblSx}>Conclusión <Box component="em" sx={{ fontStyle: 'normal', fontWeight: 500, color: '#64748b' }}>(generada; editable)</Box></Box>
            <InputBase multiline minRows={2} value={conclusion} onChange={(e) => { setConclusion(e.target.value); setEdited(true); }} inputProps={{ 'aria-label': 'Conclusión', maxLength: 10000 }} sx={inputSx} />
          </>
        )}

        {choice === 'note' && (
          <>
            <Box sx={lblSx}>Nota</Box>
            <InputBase autoFocus multiline minRows={3} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ 'aria-label': 'Nota', maxLength: 4000 }} sx={inputSx} />
          </>
        )}

        {attempted && error && <Box sx={{ fontSize: 12, color: J.dangerText, mt: '8px' }}>{error}</Box>}

        <Box sx={{ pt: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ButtonBase onClick={onClose} sx={{ height: 34, px: '14px', borderRadius: '10px', fontSize: 13, fontWeight: 700, border: '1px solid #334155', color: J.text, fontFamily: J.font }}>Cancelar</ButtonBase>
          <ButtonBase type="submit" sx={{ height: 34, px: '14px', borderRadius: '10px', fontSize: 13, fontWeight: 700, bgcolor: J.sky, color: J.onSky, fontFamily: J.font }}>{primary}</ButtonBase>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FollowUpUpdateDialog;
